import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Box,
  Button,
  Divider,
  Typography,
  Fade
} from '@mui/material';
import { Add, Close } from '@mui/icons-material';

const ContractDropdown = ({
  value,
  onChange,
  onValidationChange,
  options: propOptions,
  disabled,
  error,
  message,
  label,
  name,
  dependency,
  setFilterReady
}) => {
  const [contracts, setContracts] = useState([]);
  const [isCreatingNew, setIsCreatingNew] = useState(false);
  const [newContractId, setNewContractId] = useState('');
  const [newContractName, setNewContractName] = useState('');
  const [selectedValue, setSelectedValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [contractIdError, setContractIdError] = useState('');

  const isValidUUID = (str) => {
    const uuidRegex =
      /^[0-9A-F]{8}-[0-9A-F]{4}-4[0-9A-F]{3}-[89AB][0-9A-F]{3}-[0-9A-F]{12}$/;
    return uuidRegex.test(str);
  };

  const validateContractId = (id) => {
    if (!id) {
      setContractIdError('');
      return;
    }

    if (id.length !== 36) {
      setContractIdError(
        `Contract ID must be exactly 36 characters (you have ${id.length}). It should look like: XXXXXXXX-XXXX-XXXX-XXXX-XXXXXXXXXXXX`
      );
    } else if (!isValidUUID(id)) {
      setContractIdError(
        'Contract ID must be a valid UUID v4 format. It should look like: XXXXXXXX-XXXX-XXXX-XXXX-XXXXXXXXXXXX. All letters must be A-F and all numbers 0-9.'
      );
    } else {
      setContractIdError('');
    }
  };

  const loadContracts = useCallback(async () => {
    setIsLoading(true);
    setFilterReady?.(name, false);

    try {
      let loadedOptions = [];

      if (typeof propOptions === 'function') {
        const result = await propOptions(dependency || false);
        loadedOptions = Array.isArray(result) ? result : [];
      } else if (propOptions?.then) {
        const result = await propOptions;
        loadedOptions = Array.isArray(result) ? result : [];
      } else if (Array.isArray(propOptions)) {
        loadedOptions = propOptions;
      }

      setContracts(loadedOptions);
    } catch (error) {
      setContracts([]);
    } finally {
      setIsLoading(false);
      setFilterReady?.(name, true);
    }
  }, [propOptions, dependency, name, setFilterReady]);

  useEffect(() => {
    loadContracts();
  }, [loadContracts]);

  useEffect(() => {
    if (value?.contract_id && contracts.length > 0) {
      const existingContract = contracts.find(
        (c) => c.value === value.contract_id
      );
      if (existingContract) {
        setSelectedValue(value.contract_id);
      }
    }
  }, [value, contracts]);

  useEffect(() => {
    if (isCreatingNew && newContractId && newContractName && !contractIdError) {
      onChange(name, {
        contract_id: newContractId,
        contract_name: newContractName
      });
    }
  }, [
    newContractId,
    newContractName,
    contractIdError,
    isCreatingNew,
    name,
    onChange
  ]);

  useEffect(() => {
    if (onValidationChange) {
      const isValid =
        (selectedValue && selectedValue !== 'create-new' && !isCreatingNew) ||
        (isCreatingNew && newContractId && newContractName && !contractIdError);
      onValidationChange(isValid);
    }
  }, [
    selectedValue,
    isCreatingNew,
    newContractId,
    newContractName,
    contractIdError,
    onValidationChange
  ]);

  const handleSelectChange = (event) => {
    const selectedId = event.target.value;

    if (selectedId === 'create-new') {
      setIsCreatingNew(true);
      setSelectedValue('create-new');
      setNewContractId('');
      setNewContractName('');
      setContractIdError('');
      onChange(name, {
        contract_id: '',
        contract_name: ''
      });
    } else {
      setIsCreatingNew(false);
      setSelectedValue(selectedId);

      const selectedContract = contracts.find((c) => c.value === selectedId);
      if (selectedContract) {
        onChange(name, {
          contract_id: selectedContract.value,
          contract_name: selectedContract.name || selectedContract.display
        });
      }
    }
  };

  const handleContractIdChange = (e) => {
    const value = e.target.value.toUpperCase();
    setNewContractId(value);
    validateContractId(value);
  };

  const handleCancel = () => {
    setIsCreatingNew(false);
    setSelectedValue('');
    setNewContractId('');
    setNewContractName('');
    setContractIdError('');
    onChange(name, {
      contract_id: '',
      contract_name: ''
    });
  };

  if (!isCreatingNew) {
    return (
      <Box sx={{ width: '100%', p: 1 }}>
        <FormControl
          fullWidth
          size="small"
          disabled={disabled || isLoading}
          error={!!error}
        >
          <InputLabel
            id={`${name}-label`}
            sx={{
              '&.MuiInputLabel-shrink': {
                transform: 'translate(14px, -9px) scale(0.75)'
              }
            }}
          >
            {label}
          </InputLabel>
          <Select
            labelId={`${name}-label`}
            id={name}
            value={selectedValue}
            label={label}
            onChange={handleSelectChange}
            sx={{
              fontSize: '1rem',
              '& .MuiOutlinedInput-notchedOutline': {
                '& legend': {
                  width: 'auto'
                }
              }
            }}
          >
            {contracts.map((contract) => (
              <MenuItem
                key={contract.value}
                value={contract.value}
                sx={{ fontSize: '0.875rem' }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    width: '100%'
                  }}
                >
                  <span>{contract.display}</span>
                </Box>
              </MenuItem>
            ))}

            {contracts.length > 0 && <Divider />}

            <MenuItem
              value="create-new"
              sx={{
                color: 'primary.main',
                fontSize: '0.875rem',
                fontWeight: 500,
                '&:hover': { backgroundColor: 'action.hover' }
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Add fontSize="small" />
                <span>Create New Contract</span>
              </Box>
            </MenuItem>
          </Select>
        </FormControl>

        {message && (
          <Typography
            variant="caption"
            sx={{
              color: error ? 'error.main' : 'text.secondary',
              mt: 0.5,
              display: 'block',
              fontSize: '0.75rem'
            }}
          >
            {message}
          </Typography>
        )}
      </Box>
    );
  }

  return (
    <Fade in={isCreatingNew}>
      <Box sx={{ width: '100%', p: 1 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 1.5
          }}
        >
          <Typography
            variant="subtitle2"
            sx={{ color: 'primary.main', fontWeight: 500 }}
          >
            Create New Contract
          </Typography>
          <Button
            size="small"
            variant="text"
            onClick={handleCancel}
            startIcon={<Close fontSize="small" />}
            sx={{
              fontSize: '0.813rem',
              textTransform: 'none',
              minWidth: 'auto'
            }}
          >
            Cancel
          </Button>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <TextField
            size="small"
            label="Contract ID"
            placeholder="e.g., A1B2C3D4-E5F6-4789-A0B1-C2D3E4F5A6B7"
            value={newContractId}
            onChange={handleContractIdChange}
            variant="outlined"
            fullWidth
            required
            error={!!contractIdError}
            helperText={
              contractIdError ||
              'Must be a 36-character UUID v4 (8-4-4-4-12 format)'
            }
            sx={{
              '& .MuiInputBase-input': {
                fontSize: '0.875rem',
                fontFamily: 'monospace',
                letterSpacing: '0.5px'
              },
              '& .MuiInputLabel-root': { fontSize: '0.875rem' },
              '& .MuiFormHelperText-root': {
                fontSize: '0.75rem',
                color: contractIdError ? 'error.main' : 'text.secondary'
              }
            }}
          />

          <TextField
            size="small"
            label="Contract Name"
            placeholder="e.g., Tier 0 Leg"
            value={newContractName}
            onChange={(e) => setNewContractName(e.target.value)}
            variant="outlined"
            fullWidth
            required
            helperText="Enter a descriptive name for this contract"
            sx={{
              '& .MuiInputBase-input': { fontSize: '0.875rem' },
              '& .MuiInputLabel-root': { fontSize: '0.875rem' },
              '& .MuiFormHelperText-root': {
                fontSize: '0.75rem',
                color: 'text.secondary'
              }
            }}
          />

          {newContractId && newContractName && !contractIdError && (
            <Typography
              variant="caption"
              sx={{
                color: 'success.main',
                fontSize: '0.75rem',
                fontStyle: 'italic'
              }}
            >
              ✓ Contract is valid and ready to submit
            </Typography>
          )}
        </Box>
      </Box>
    </Fade>
  );
};

ContractDropdown.propTypes = {
  value: PropTypes.shape({
    contract_id: PropTypes.string,
    contract_name: PropTypes.string
  }),
  onChange: PropTypes.func.isRequired,
  onValidationChange: PropTypes.func,
  options: PropTypes.oneOfType([
    PropTypes.array,
    PropTypes.func,
    PropTypes.object
  ]),
  disabled: PropTypes.bool,
  error: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  message: PropTypes.string,
  label: PropTypes.string,
  name: PropTypes.string,
  dependency: PropTypes.any,
  setFilterReady: PropTypes.func
};

ContractDropdown.defaultProps = {
  value: {
    contract_id: '',
    contract_name: ''
  },
  disabled: false,
  error: false,
  message: '',
  label: 'QuoteWizard Contract',
  name: 'contract'
};

export default ContractDropdown;
